import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";

/**
 * Stand-in for an option whose value is empty.
 *
 * Radix reserves the empty string on a Select item: it is how the *Select* is
 * told to clear itself, so an item carrying it throws. Both `null` and `""` are
 * legitimate option values here — `SimpleSelectOption` permits null outright,
 * and a "— none —" entry is the ordinary way to offer no choice — so the empty
 * value is encoded on the way into Radix and decoded on the way out. Callers
 * keep their own spelling of empty and never see this string.
 */
const EMPTY_VALUE = "__simple_select_empty__";

function isEmptyValue(value: string | number | null | undefined): boolean {
  return value === null || value === undefined || value === "";
}

export type SimpleSelectOption<
  T extends string | number | null = string | number,
> = {
  label: string;
  value: T;
  disabled?: boolean;
  description?: string;
  icon?: React.ReactNode;
};

type ValueCallback<T> = T extends string | number | null
  ? string | number | null
  : string | number;

export interface SimpleSelectProps<
  T extends string | number | null = string | number,
> {
  options: SimpleSelectOption<T>[];
  value?: string | number | null;
  placeholder?: string;
  onChange?: (value: ValueCallback<T>) => void;
  disabled?: boolean;
  /** Extra classes for the trigger (e.g. a smaller text size). */
  triggerClassName?: string;
  /** Extra classes applied to every option in the list. */
  itemClassName?: string;
}

export function SimpleSelect<
  T extends string | number | null = string | number,
>({
  options,
  value,
  placeholder,
  onChange,
  disabled,
  triggerClassName,
  itemClassName,
}: SimpleSelectProps<T>) {
  const selectedOption = options.find((o) =>
    isEmptyValue(value) ? isEmptyValue(o.value) : o.value === value,
  );
  const emptyOption = options.find((o) => isEmptyValue(o.value));

  // With no empty option to select, an empty value means "nothing chosen" and
  // Radix should fall through to the placeholder rather than match an item.
  const selectValue = isEmptyValue(value)
    ? emptyOption
      ? EMPTY_VALUE
      : undefined
    : String(value);

  return (
    <Select
      value={selectValue}
      onValueChange={(val) => {
        if (val === EMPTY_VALUE) {
          // Hand back the option's own spelling of empty — null and "" are not
          // interchangeable to the caller.
          onChange?.((emptyOption ? emptyOption.value : "") as never);
          return;
        }
        const option = options.find(
          ({ value }) => value === val || value === Number(val),
        );
        onChange?.(option?.value ?? val);
      }}
      disabled={disabled}
    >
      <SelectTrigger className={triggerClassName}>
        <SelectValue placeholder={placeholder}>
          {selectedOption ? (
            <SelectItemInner option={selectedOption} labelOnly />
          ) : (
            placeholder
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem
            key={isEmptyValue(option.value) ? EMPTY_VALUE : String(option.value)}
            value={
              isEmptyValue(option.value) ? EMPTY_VALUE : String(option.value)
            }
            className={cn(
              option.disabled && "text-muted-foreground cursor-default!",
              itemClassName,
            )}
            disabled={option.disabled}
          >
            <SelectItemInner option={option} />
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SelectItemInner<T extends string | number | null>({
  option,
  labelOnly,
}: {
  option: SimpleSelectOption<T>;
  labelOnly?: boolean;
}) {
  let content: React.ReactNode = option.label;
  if (labelOnly) {
    return content;
  }

  if (option.description) {
    content = (
      <div className="flex flex-col gap-1">
        {content}
        <div className="text-sm text-muted-foreground">
          {option.description}
        </div>
      </div>
    );
  }
  if (option.icon) {
    content = (
      <div className="flex items-center gap-2">
        {option.icon} {content}
      </div>
    );
  }
  return content;
}
